import styled from '@emotion/styled';
import {
  faBrakeWarning,
  faCircleCheck,
  faFileCertificate,
  faLandmarkMagnifyingGlass,
} from '@fortawesome/duotone-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';

import type { DtoV1AUSAusRequestHistoryData } from '~/__generated__/api';
import Box from '~/common/components/Box';
import useCreditReportLinks from '~/common/components/Credit/useCreditReportLinks';
import List from '~/common/components/Lists/List';
import ListItem from '~/common/components/Lists/ListItem';
import Overflow from '~/common/components/Overflow';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import Text from '~/common/components/Texts/Text';
import { UNDERWRITING_METHODS } from '~/common/constants/api-ids';
import { useGetAUSReportLinks } from '~/common/hooks/useGetAUSReportLinks';
import Button from '~/common/mui/Buttons/MUIButton/MUIButton';
import Link from '~/common/mui/Link';
import { isAUSStatusSuccess, openAUSReport } from '~/modals/AUS/ausUtils';
import { useAUSDetailsQuery } from '~/modals/AUS/hooks/useAUSDetailsQuery';
import palette from '~/theme/mui/palette';

function ausLabel(
  ausMethod:
    | (typeof UNDERWRITING_METHODS)[keyof typeof UNDERWRITING_METHODS]
    | 3,
) {
  return ausMethod === UNDERWRITING_METHODS.DesktopUnderwriter ? 'DU' : 'LPA';
}

function StatusIcon({ status }) {
  const isSuccess = isAUSStatusSuccess(status);

  return (
    <FontAwesomeIcon
      icon={isSuccess ? faCircleCheck : faBrakeWarning}
      style={{
        color: isSuccess ? palette.green[500] : palette.red[600],
        marginRight: '12px',
        fontSize: '1.5rem',
        boxSizing: 'content-box',
        maxWidth: '24px',
        maxHeight: '24px',
        minWidth: '24px',
        minHeight: '24px',
      }}
    />
  );
}

function PreviousResult({
  result,
}: Readonly<{ result: DtoV1AUSAusRequestHistoryData }>) {
  if (!result) {
    return null;
  }

  return (
    <Box>
      <Text as="h3">Previous Result</Text>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingTop: '0.875rem',
        }}
      >
        <StatusIcon status={result.recommendation} />
        <Text as="span">{result.recommendation}</Text>
      </Box>
    </Box>
  );
}

function PreviousRunInfo({
  result,
}: Readonly<{ result: DtoV1AUSAusRequestHistoryData }>) {
  if (!result) {
    return null;
  }

  return (
    <Box>
      <Text as="h3">Run Info</Text>
      <Text sx={{ marginTop: '6px' }}>
        {result.requestMethod === UNDERWRITING_METHODS.DesktopUnderwriter
          ? 'DU'
          : 'LPA'}
      </Text>
      <Text sx={{ color: palette.gray[500], fontSize: '0.75rem' }}>
        {result.requestMethod === UNDERWRITING_METHODS.DesktopUnderwriter
          ? 'Desktop Underwriter'
          : 'Loan Product Advisor'}
      </Text>
      <Text sx={{ marginTop: '6px', color: palette.black }}>
        {result.requestShortDate}
      </Text>
      <Text sx={{ color: palette.gray[500], fontSize: '0.75rem' }}>
        {result.requestTime}
      </Text>
      <Text sx={{ marginTop: '6px', color: palette.black }}>
        {result.requestUser}
      </Text>
    </Box>
  );
}

function Findings({
  result,
}: Readonly<{ result: DtoV1AUSAusRequestHistoryData }>) {
  const showSnackbar = useSnackbar();
  const isFindingsEnabled = ['Approve/Eligible', 'Accept'].some((status) =>
    result.recommendation?.includes(status),
  );

  const findingsQuery = useGetAUSReportLinks({
    requestId: result?.requestIdentifier?.requestId,
    ausMethod: result?.requestMethod,
    requestDateTime: result?.requestDate,
    reportType: 'findings',
    requestStatus:
      result.requestMethod === UNDERWRITING_METHODS.LoanProductAdvisor
        ? isFindingsEnabled
        : null,
    enabled: false,
  });

  useEffect(() => {
    if (!result?.requestMethod || !findingsQuery.isError) {
      return;
    }

    showSnackbar({
      severity: 'error',
      message: `${ausLabel(result.requestMethod)} Findings failed to run`,
      duration: 6000,
    });
  }, [findingsQuery.isError, result?.requestMethod, showSnackbar]);

  return (
    <Box>
      <Button
        variant="text"
        startIcon={
          <FontAwesomeIcon
            icon={faLandmarkMagnifyingGlass}
            style={{
              fontSize: '1.5rem',
              boxSizing: 'content-box',
              maxWidth: '24px',
              maxHeight: '24px',
              minWidth: '24px',
              minHeight: '24px',
              marginRight: '4px',
            }}
          />
        }
        sx={{ padding: 'unset' }}
        onClick={(e) => {
          e.preventDefault();
          return openAUSReport(
            findingsQuery.refetch,
            `${ausLabel(result.requestMethod)} Findings`,
          );
        }}
        isLoading={findingsQuery.isFetching}
      >
        {result.requestMethod === UNDERWRITING_METHODS.DesktopUnderwriter
          ? 'DU'
          : 'LPA'}{' '}
        Findings
      </Button>
    </Box>
  );
}

function CreditReportLinks({
  result,
}: Readonly<{ result: DtoV1AUSAusRequestHistoryData }>) {
  const isEnabled = result.requestStatus === 'Processed Data';
  const { data, isLoading } = useCreditReportLinks(
    result.requestMethod === UNDERWRITING_METHODS.DesktopUnderwriter
      ? 'DU'
      : 'LPA',
    result.requestIdentifier.requestId,
    isEnabled,
  );

  const StyledIcon = styled(FontAwesomeIcon)`
    width: 1.5rem;
    height: 1.5rem;
    box-sizing: content-box;
    color: ${({ theme }) => theme.colors.blue[600]};
  `;

  if (isLoading) {
    return null;
  }

  if (!isEnabled || !data?.creditReportLinks?.length) {
    return (
      <Box>
        <Button
          variant="text"
          component={Link}
          startIcon={
            <FontAwesomeIcon
              icon={faFileCertificate}
              style={{
                fontSize: '1.5rem',
                boxSizing: 'content-box',
                maxWidth: '24px',
                maxHeight: '24px',
                minWidth: '24px',
                minHeight: '24px',
                marginRight: '4px',
              }}
            />
          }
          sx={{ padding: 'unset' }}
          isDisabled
        >
          Credit Report
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'calc(1.5rem + 12px) minmax(0, 1fr)',
      }}
    >
      <StyledIcon icon={faFileCertificate} />
      <Text>Credit Report</Text>
      <List sx={{ display: 'contents' }}>
        {data.creditReportLinks.map((link) => (
          <ListItem
            key={link.customerIdentifiers[0].customerRecordId}
            sx={{
              textDecoration: 'underline',
              gridColumn: '2 / 3',
              minWidth: 0,
            }}
          >
            <Link
              href={link.creditReportUri}
              target="_blank"
              sx={{ width: '100%', minWidth: 0 }}
            >
              <Overflow>{link.displayName}</Overflow>
            </Link>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

export default function RunAUSLeftColumn({
  selectedOption,
}: Readonly<{ selectedOption: 'DU' | 'LPA' }>) {
  const { data: ausDetails } = useAUSDetailsQuery();
  const lastResult = ausDetails?.lastAusRunDetails;
  const selectedResult =
    (() => {
      switch (selectedOption) {
        case 'DU':
          return ausDetails?.lastAusMethodDetails?.desktopUnderwriterDetails;
        case 'LPA':
          return ausDetails?.lastAusMethodDetails?.loanProductAdvisorDetails;
      }
    })() ?? lastResult;

  if (!selectedResult) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '9.5rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pb: '1.5rem',
          gap: '0.75rem',
        }}
      >
        <PreviousResult result={selectedResult} />
        <CreditReportLinks result={selectedResult} />
        <Findings result={selectedResult} />
      </Box>
      <PreviousRunInfo result={selectedResult} />
    </Box>
  );
}
